import { useEffect, useSyncExternalStore } from 'react';

/** Minimal client-side routing: a handful of routes does not need a router library. */
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') window.addEventListener('popstate', notify);

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  window.scrollTo(0, 0);
  notify();
}

export function usePath(): string {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => window.location.pathname + window.location.search,
    () => '/',
  );
}

export type Route =
  | { name: 'home' }
  | { name: 'project'; id: string }
  | { name: 'exercise'; slug: string }
  | { name: 'new' }
  | { name: 'session'; code: string }
  | { name: 'rewards' }
  | { name: 'login' }
  | { name: 'signup' }
  | { name: 'forgot' }
  | { name: 'reset'; token: string }
  | { name: 'verify'; token: string }
  | { name: 'consent'; token: string }
  | { name: 'parent'; token: string }
  | { name: 'privacy' }
  | { name: 'account' }
  | { name: 'admin' }
  | { name: 'classes' }
  | { name: 'class'; id: string }
  | { name: 'join'; code: string }
  | { name: 'homework'; id: string }
  | { name: 'assignment'; id: string }
  | { name: 'review'; assignmentId: string; userId: string }
  | { name: 'myExercises' }
  | { name: 'myExercise'; slug: string | null }
  | { name: 'notFound' };

export function matchRoute(path: string): Route {
  const [pathname] = path.split('?');
  const parts = (pathname ?? '/').split('/').filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { name: 'home' };
  if (parts[0] === 'p' && parts[1]) return { name: 'project', id: parts[1] };
  if (parts[0] === 'e' && parts[1]) return { name: 'exercise', slug: parts[1] };
  if (parts[0] === 'new' && parts.length === 1) return { name: 'new' };
  if (parts[0] === 'me' && parts.length === 1) return { name: 'rewards' };
  if (parts[0] === 's' && parts[1]) return { name: 'session', code: parts[1] };
  const [first, second, third] = parts;
  if (parts.length === 1) {
    const simple = {
      login: 'login',
      signup: 'signup',
      forgot: 'forgot',
      privacy: 'privacy',
      account: 'account',
      admin: 'admin',
      classes: 'classes',
      'my-exercises': 'myExercises',
    } as const;
    const name = simple[first as keyof typeof simple];
    if (name) return { name };
  }
  if (parts.length === 2 && second) {
    if (first === 'reset') return { name: 'reset', token: second };
    if (first === 'verify') return { name: 'verify', token: second };
    if (first === 'consent') return { name: 'consent', token: second };
    if (first === 'parent') return { name: 'parent', token: second };
    if (first === 'classes') return { name: 'class', id: second };
    if (first === 'join') return { name: 'join', code: second };
    if (first === 'homework') return { name: 'homework', id: second };
    if (first === 'assignments') return { name: 'assignment', id: second };
    if (first === 'my-exercises') {
      return { name: 'myExercise', slug: second === 'new' ? null : second };
    }
  }
  if (parts.length === 3 && first === 'review' && second && third) {
    return { name: 'review', assignmentId: second, userId: third };
  }
  return { name: 'notFound' };
}

/** Plain left-clicks on internal links navigate without a reload. */
export function onLinkClick(e: React.MouseEvent<HTMLAnchorElement>): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return;
  }
  const href = e.currentTarget.getAttribute('href');
  if (!href || !href.startsWith('/')) return;
  e.preventDefault();
  navigate(href);
}

/** Page title: "Sum of digits · PebbleCode". */
export function useTitle(title: string | null): void {
  useEffect(() => {
    document.title = title ? `${title} · PebbleCode` : 'PebbleCode';
  }, [title]);
}

/** Where to go after logging in: `?next=/join/ABC` (same-site paths only). */
export function nextPath(): string {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export const withNext = (path: string) => {
  const next = nextPath();
  return next === '/' ? path : `${path}?next=${encodeURIComponent(next)}`;
};
